/**
 * Resolve which requirement the detail panel should show.
 *
 * The left list is filtered (keyword + conditions) and also narrowed by the
 * active subcategory tab, so the reqId carried by the URL can legitimately be
 * absent from it. Falling back to `null` in that case silently replaces the
 * detail card with “请从左侧选择一个需求查看详情”, which looks like a broken page.
 *
 * This module is the single source of truth for that decision:
 * - none    → nothing selected, or data not loaded yet (keep neutral placeholder)
 * - visible → selected req exists in the visible list
 * - hidden  → selected req exists but is filtered out / on another tab
 * - missing → selected req does not exist anywhere (deleted or bad URL)
 */

import type { Requirement } from '@/types';

export type RequirementSelection =
  | { kind: 'none' }
  | { kind: 'visible'; requirement: Requirement }
  | { kind: 'hidden'; requirement: Requirement }
  | { kind: 'missing'; reqId: string };

export function resolveRequirementSelection(
  selectedReqId: string | null | undefined,
  visibleRequirements: readonly Requirement[],
  knownRequirements: readonly Requirement[] = [],
): RequirementSelection {
  if (!selectedReqId) return { kind: 'none' };

  // Visible list wins: an item shown in the list must never be reported as hidden.
  const visible = visibleRequirements.find((r) => r.reqId === selectedReqId);
  if (visible) return { kind: 'visible', requirement: visible };

  const known = knownRequirements.find((r) => r.reqId === selectedReqId);
  if (known) return { kind: 'hidden', requirement: known };

  // Both lists empty means data has not loaded (or is mid-reset). Do not claim
  // the requirement is missing in that window.
  if (visibleRequirements.length === 0 && knownRequirements.length === 0) {
    return { kind: 'none' };
  }

  return { kind: 'missing', reqId: selectedReqId };
}

/** The requirement to render in the detail panel, if any. */
export function selectionRequirement(selection: RequirementSelection): Requirement | null {
  return selection.kind === 'visible' || selection.kind === 'hidden'
    ? selection.requirement
    : null;
}
